import { cn } from "@/lib/utils";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faFilter } from "@nnr/fontawesome-sharp-light/icons/sharp/light";
import * as React from "react";
import { Button } from "../ui/button";
import { Checkbox } from "../ui/checkbox";
import { SearchInput } from "../ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "../ui/popover";

interface GroupedTableFiltersProps {
  // Search props
  searchText: string;
  onSearchChange: (value: string) => void;

  // Group filter props
  groupLabels: string[];
  selectedGroups: Set<string>;
  onGroupFilterChange: (groupLabel: string, selected: boolean) => void;
  onClearAllFilters: () => void;

  // State
  hasActiveFilters: boolean;
  activeFilterCount: number;
}

export function GroupedTableFilters({
  searchText,
  onSearchChange,
  groupLabels,
  selectedGroups,
  onGroupFilterChange,
  onClearAllFilters,
  hasActiveFilters,
  activeFilterCount,
}: GroupedTableFiltersProps) {
  const [isFilterOpen, setIsFilterOpen] = React.useState(false);

  return (
    <div className="flex flex-row items-center gap-2">
      <SearchInput
        value={searchText}
        onChange={(e) => onSearchChange(e.target.value)}
        placeholder="Search..."
      />

      {groupLabels.length > 1 && (
        <Popover open={isFilterOpen} onOpenChange={setIsFilterOpen}>
          <PopoverTrigger asChild>
            <Button
              variant="ghost"
              size="icon"
              className={cn("relative", {
                "text-primary": hasActiveFilters,
              })}
              title="Filters"
              aria-label="Filters"
            >
              <FontAwesomeIcon icon={faFilter} />
              {activeFilterCount > 0 && (
                <span className="bg-primary text-primary-foreground absolute -top-1 -right-1 flex h-4 w-4 items-center justify-center rounded-full text-xs">
                  {activeFilterCount}
                </span>
              )}
            </Button>
          </PopoverTrigger>
          <PopoverContent className="w-80" align="end">
            <div className="space-y-4">
              {/* Header */}
              <div className="flex items-center justify-between">
                <h4 className="font-medium">Filters</h4>
                {hasActiveFilters && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={onClearAllFilters}
                    className="text-xs"
                  >
                    Clear all
                  </Button>
                )}
              </div>

              <div className="space-y-2">
                <h5 className="text-sm font-medium">Groups</h5>
                <div className="max-h-40 space-y-1 overflow-y-auto">
                  {groupLabels.map((groupLabel, index) => {
                    const optionId = `filter-group-${index}`;
                    const isSelected = selectedGroups.has(groupLabel);

                    return (
                      <div key={index} className="flex items-center space-x-2">
                        <Checkbox
                          id={optionId}
                          checked={isSelected}
                          onCheckedChange={(checked) =>
                            onGroupFilterChange(groupLabel, !!checked)
                          }
                        />
                        <label
                          htmlFor={optionId}
                          className="flex-1 cursor-pointer truncate text-sm capitalize"
                          title={groupLabel}
                        >
                          {groupLabel}
                        </label>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          </PopoverContent>
        </Popover>
      )}
    </div>
  );
}
